import { t, localeTag } from './i18n.js';
import { useEffect, useState } from 'react';
import { api, send } from './api.js';
import { Notice } from './components.js';
export function Operations() {
  const [status, setStatus] = useState<any>(),
    [recoveries, setRecoveries] = useState<any[]>([]),
    [operatorRequests, setOperatorRequests] = useState<any[]>([]),
    [notice, setNotice] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const [s, r, o] = await Promise.all([
        api('/platform/operations'),
        api('/platform/recoveries'),
        api('/platform/operators/requests'),
      ]);
      setStatus(s);
      setRecoveries(r);
      setOperatorRequests(o);
    } catch (e: any) {
      setError(e.message);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const submit = async (event: React.FormEvent<HTMLFormElement>, path: string) => {
    event.preventDefault();
    const form = event.currentTarget;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await send(path, Object.fromEntries(new FormData(form)));
      if (path === '/platform/operators')
        setNotice(
          result?.status === 'requested'
            ? t('Demande transmise : un second opérateur doit l’approuver.')
            : t('Invitation envoyée.'),
        );
      form.reset();
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="admin-card operations-card">
      <div className="section-heading">
        <h2>{t('Exploitation du laboratoire')}</h2>
        <button className="secondary" onClick={() => void load()}>
          {t('Actualiser')}
        </button>
      </div>
      {error && <Notice type="error">{error}</Notice>}
      {notice && <Notice type="success">{notice}</Notice>}
      <p>{t('Les mesures locales sont distinctes de la qualification de production.')}</p>
      {status && (
        <>
          <p>
            {t(
              '{samples} mesures de disponibilité de l’API · {days} jours observés · qualification de production en attente.',
              {
                samples: status.observation.samples,
                days: status.observation.observedDays.toFixed(2),
              },
            )}
          </p>
          <ul className="operations-services">
            {status.current.map((s: any) => (
              <li key={s.name}>
                <div>
                  <strong>
                    {{
                      identity: t('Identité et connexion'),
                      webmail: t('API du webmail'),
                      'tls-mail': t('Certificat de messagerie'),
                    }[s.name as string] ?? s.name}
                  </strong>
                  <span className={s.current_ok ? 'status-good' : 'status-warning'}>
                    {s.current_ok ? t('Disponible') : t('Incident')}
                  </span>
                </div>
                <small>{s.payload.detail}</small>
              </li>
            ))}
          </ul>
          <p>
            {t('Écritures à rapprocher : {n}.', {
              n: status.writes.reduce((sum: number, r: any) => sum + r.count, 0),
            })}{' '}
            {t('Dernier export d’audit :')}{' '}
            {status.audit?.exported_at
              ? new Date(status.audit.exported_at).toLocaleString(localeTag())
              : t('Pas encore exporté')}
            .
          </p>
        </>
      )}
      <details>
        <summary>{t('Inviter un opérateur')}</summary>
        <form className="stack-form" onSubmit={(e) => void submit(e, '/platform/operators')}>
          <label>
            {t('Nom')}
            <input name="name" minLength={2} required />
          </label>
          <label>
            {t('Adresse professionnelle')}
            <input name="email" type="email" required />
          </label>
          <label>
            {t('Adresse personnelle de récupération indépendante')}
            <input name="recoveryEmail" type="email" required />
          </label>
          <button className="primary" disabled={busy}>
            {t('Créer l’invitation')}
          </button>
        </form>
      </details>
      {operatorRequests.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('Opérateur à inviter')}</th>
                <th>{t('Expire le')}</th>
                <th>{t('Action')}</th>
              </tr>
            </thead>
            <tbody>
              {operatorRequests.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.name} · {r.email}
                    <small>{t('Demande de {email}', { email: r.requester_email })}</small>
                  </td>
                  <td>{new Date(r.expires_at).toLocaleString(localeTag())}</td>
                  <td>
                    {r.canApprove ? (
                      <button
                        className="secondary"
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          setError('');
                          try {
                            await send(`/platform/operators/requests/${r.id}/approve`, {});
                            setNotice(t('Invitation envoyée.'));
                            await load();
                          } catch (e: any) {
                            setError(e.message);
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        {t('Approuver l’invitation')}
                      </button>
                    ) : (
                      t('En attente d’un second opérateur')
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <details>
        <summary>{t('Préparer une récupération après perte des facteurs')}</summary>
        <p>
          {t(
            'Vérifiez l’identité par le canal indépendant de l’entreprise. Un second opérateur devra approuver ; cette approbation révoque les accès existants.',
          )}
        </p>
        <form className="stack-form" onSubmit={(e) => void submit(e, '/platform/recoveries')}>
          <label>
            {t('Adresse professionnelle du collaborateur')}
            <input name="email" type="email" required />
          </label>
          <label>
            {t('Référence de la vérification effectuée')}
            <input name="verificationRef" minLength={12} required />
          </label>
          <button className="primary" disabled={busy}>
            {t('Soumettre au second opérateur')}
          </button>
        </form>
      </details>
      {recoveries.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('Collaborateur')}</th>
                <th>{t('État')}</th>
                <th>{t('Action')}</th>
              </tr>
            </thead>
            <tbody>
              {recoveries.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.email}
                    <small>{t('Demande de {email}', { email: r.requester_email })}</small>
                  </td>
                  <td>
                    {{
                      requested: t('À approuver'),
                      approved: t('Approuvée'),
                      executing: t('Réinitialisation en cours'),
                      invitation_pending: t('Réenrôlement attendu'),
                      completed: t('Terminée'),
                      review_required: t('À réexaminer'),
                      failed: t('Échec'),
                    }[r.status as string] ?? r.status}
                  </td>
                  <td>
                    {r.canApprove && (
                      <button
                        className="secondary"
                        disabled={busy}
                        onClick={async () => {
                          if (
                            !window.confirm(
                              t(
                                'Approuver cette récupération ? Les accès existants de {email} seront révoqués.',
                                { email: r.email },
                              ),
                            )
                          )
                            return;
                          setBusy(true);
                          try {
                            await send(`/platform/recoveries/${r.id}/approve`, {});
                            await load();
                          } catch (e: any) {
                            setError(e.message);
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        {t('Approuver et révoquer les accès')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
